import { HEIGHT_CONTROL_PANEL_UUID } from '../constants';
import SlotService from '../services/SlotService';
import { Article, BaseArticle, RequiredArticle } from './Article';
import { ConfigurationCode } from './ConfigurationCode';
import { Dimensions } from './Dimensions';
import { isSlotLine, Slot } from './Slot';
import { SlotDefinition, SlotType } from './SlotDefinition';
import { SocketType } from './SocketType';
import { Theme } from './Theme';

/**
 * Representation of what user has configured.
 * Keeps all information to assemble the articles into "product" fitting customer requirements
 */
export class Configuration {
  public constructor(
    root: string,
    articles: Article[],
    slots: Slot[],
    id: string,
    requiredConnectArticles: RequiredArticle[],
    requiredLightingArticles: RequiredArticle[],
    creator: string,
    socketType: SocketType,
    theme: Theme,
    version: number,
    configurationCode?: ConfigurationCode,
    createdSessionId?: string,
  ) {
    this.root = root;
    this.articles = articles;
    this.slots = slots;
    this.id = id;
    this.requiredConnectArticles = requiredConnectArticles;
    this.requiredLightingArticles = requiredLightingArticles;
    this.creator = creator;
    this.socketType = socketType;
    this.theme = theme;
    this.version = version;
    this.configurationCode = configurationCode;
    this.createdSessionId = createdSessionId;
  }

  // Id of the root article in articles array
  // Starting point of configuration
  root: string;
  // All articles of the configuration
  articles: Article[];
  // All slots of the configuration
  slots: Slot[];
  // UUID under which this configuration is stored in the configuration-store
  id: string;
  // All articles which are required in order to connect other articles
  requiredConnectArticles: RequiredArticle[];
  // All articles which are required for lighting articles in order to work properly
  requiredLightingArticles: RequiredArticle[];
  // Creator of the configuration in configuration store
  creator: string;
  // Code of the configuration from the project service or undefined if it is a draft configuration
  configurationCode?: ConfigurationCode;
  // Width, height and depth of the entire assembled product
  dimensions?: Dimensions;
  // Country specific socket type
  socketType: SocketType;
  // Color scheme of the configuration. Affects newly added articles as well
  theme: Theme;
  // Current version of the configuration
  version: number;

  createdSessionId: string;

  getRoot(): Article {
    const root = this.articles.find((article) => article.id === this.root);
    if (!root) {
      throw new Error(`Root article ${this.root} cannot be found in the articles.`);
    }
    return root;
  }

  getArticlesByArticleNumber(number: string): BaseArticle[] {
    return this.getAllArticles().filter((article) => article.number === number);
  }

  getArticle(id: string): Article | undefined {
    return this.articles.find((article) => article.id === id);
  }

  getRequiredLightingArticle(id: string): RequiredArticle | undefined {
    return this.requiredLightingArticles.find((article) => article.id === id);
  }

  getParentArticle(id: string): Article | undefined {
    const slot = this.getAttachedSlot(id);
    return slot && slot.parentId ? this.getArticle(slot.parentId) : undefined;
  }

  getChildren(id: string): string[] {
    return this.slots
      .filter((slot) => slot.parentId === id)
      .reduce((articleIds, slot) => {
        if (isSlotLine(slot)) {
          return [
            ...articleIds,
            ...slot.children
              .filter((child) => child.id !== HEIGHT_CONTROL_PANEL_UUID)
              .map((child) => child.id),
          ];
        }

        if (slot.childId) {
          return [...articleIds, slot.childId];
        }

        return articleIds;
      }, []);
  }

  getAttachedSlot(articleId: string): Slot | undefined {
    return this.slots.find((slot) => SlotService.slotHasChild(slot, articleId));
  }

  getBlockedSlots(articleId: string): Slot[] {
    return this.slots.filter((slot) => slot.blockedBy === articleId);
  }

  getSlotsOfArticle(articleId: string): Slot[] {
    return this.slots.filter((slot) => slot.parentId === articleId);
  }

  getSlotDefinitionsOfArticle(articleId: string): SlotDefinition[] {
    return this.getSlotsOfArticle(articleId).map((slot) => slot.definition);
  }

  getOccupiedChildrenSlots(id: string): Slot[] {
    return this.slots
      .filter((slot) => slot.parentId === id)
      .filter((slot) => SlotService.isSlotOccupied(slot));
  }

  getRequiredArticles(): RequiredArticle[] {
    return [...this.requiredConnectArticles, ...this.requiredLightingArticles];
  }

  getAllArticles(): BaseArticle[] {
    return [...this.articles, ...this.getRequiredArticles()];
  }

  isDraft(): boolean {
    return !this.configurationCode;
  }

  readableRootArticleName() {
    const root = this.getRoot();
    return `${root.definition.displayName} ${root.definition.size}mm`;
  }

  getPositionOfHeightControlPanel(): number | undefined {
    const root = this.getRoot();
    if (!root.definition.hasHeightControlPanel) {
      return undefined;
    }

    const heightControlPanels = this.slots
      .filter((slot) => slot.parentId === root.id)
      .filter(isSlotLine)
      .flatMap((slot) => slot.children)
      .filter((slot) => slot.id === HEIGHT_CONTROL_PANEL_UUID);

    if (heightControlPanels.length !== 1) {
      throw new Error(
        `Found ${heightControlPanels.length} height control panels but there should be only one.`,
      );
    }

    return heightControlPanels[0].position;
  }

  getAmountOfArticles(): number {
    return (
      this.articles.length +
      this.requiredConnectArticles.reduce((prev, current) => prev + current.amount, 0) +
      this.requiredLightingArticles.reduce((prev, current) => prev + current.amount, 0)
    );
  }

  findParentId(id: string): string {
    let parentSlotId = undefined;
    this.slots.forEach((slot) => {
      if (slot.type === SlotType.LINE && slot.children.map((child) => child.id).includes(id)) {
        parentSlotId = slot.id;
      } else if (
        (slot.type === SlotType.SINGLE || slot.type === SlotType.PAIR) &&
        slot.childId === id
      ) {
        parentSlotId = slot.childId;
      }
    });
    if (!parentSlotId) {
      this.articles.forEach((article) => {
        if (
          article.requiredConnectArticleId?.includes(id) ||
          article.requiredLightingArticlesIds?.includes(id)
        ) {
          parentSlotId = article.id;
        }
      });
    }
    return parentSlotId;
  }
}
